// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useAskV2ReviewCommentFilter.test.tsx
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { type ReviewSourceFilterOption } from "./AskV2ReviewTagFilter";
import {
  type CommentWithOptionalTags,
  defaultActiveReviewCommentTags,
  deriveReviewCommentFilter,
  isReviewCommentFilterActive,
  isReviewCommentVisible,
  PUNTT_FINDINGS_SOURCE,
  type ReviewCommentSourceKey,
  type ReviewCommentTagId,
  reviewExpertFilterOptions,
  type SavedReviewCommentFilter,
} from "./reviewCommentTags";
import {
  EVERY_STATUS,
  type ReviewListStatusFilter,
  statusFilterNarrows,
} from "./useReviewListView";

import { getUsers } from "../../services/users";
import {
  type ReviewCommentGroup,
  reviewCommentGroup,
} from "../../utils/visibleReviewComments";
import { saveAskPunttReviewCommentFilter } from "../services/reviews";

type FilterState = {
  activeTags: Set<ReviewCommentTagId>;
  alwaysOn: boolean;
  hiddenSources: Set<ReviewCommentSourceKey>;
};

type Options = {
  comments: readonly CommentWithOptionalTags[];
  authorNameByUserId?: Readonly<Record<string, string>>;
  reviewId?: string;
  viewerUserId?: string;
  startedBy?: string | null;
  selectedReviewerIds?: readonly string[];
  selectedExpertIds?: readonly string[];
  savedFilter?: SavedReviewCommentFilter | null;
  onSaveFilter?: (filter: SavedReviewCommentFilter) => void;
};

/**
 * The findings filter for one review: which types are checked, which raisers
 * ("Puntt", or one expert's review) are hidden, and whether a comment passes.
 */
export function useAskV2ReviewCommentFilter({
  comments,
  authorNameByUserId,
  reviewId,
  viewerUserId,
  startedBy,
  selectedReviewerIds,
  selectedExpertIds,
  savedFilter,
  onSaveFilter,
}: Options) {
  const { t } = useTranslation("ui");
  const [edited, setEdited] = useState<FilterState | null>(null);
  // why: held here, not in the list, so the Comments tab, the pin count and the
  // artwork pins count only the statuses the Filter menu lets through.
  const [statuses, setStatuses] =
    useState<ReadonlySet<ReviewCommentGroup>>(EVERY_STATUS);

  const expertOptions = useMemo(
    () => reviewExpertFilterOptions(comments, authorNameByUserId),
    [authorNameByUserId, comments],
  );
  const expertKeys = useMemo(
    () => expertOptions.map((expert) => expert.key),
    [expertOptions],
  );
  const derived = useMemo(
    () =>
      deriveReviewCommentFilter({
        viewerUserId,
        startedBy,
        selectedReviewerIds,
        selectedExpertIds,
        saved: savedFilter,
        expertKeys,
      }),
    [
      expertKeys,
      savedFilter,
      selectedExpertIds,
      selectedReviewerIds,
      startedBy,
      viewerUserId,
    ],
  );
  const savedKey = useMemo(() => {
    if (savedFilter == null) return "";
    return JSON.stringify({
      activeTags: [...savedFilter.activeTags].sort(),
      alwaysOn: savedFilter.alwaysOn,
      hiddenSources: [...savedFilter.hiddenSources].sort(),
    });
  }, [savedFilter]);
  // why: a new review, viewer, or saved row from the server must drive the
  // boxes; a stale local copy that diverged from commentFilters hid comments
  // while every box looked off, or showed Filtered with nothing checked.
  useEffect(() => {
    setEdited(null);
  }, [reviewId, viewerUserId, savedKey]);
  useEffect(() => {
    setStatuses(EVERY_STATUS);
  }, [reviewId]);
  const activeTags = edited?.activeTags ?? derived.activeTags;
  const alwaysOn = edited?.alwaysOn ?? derived.alwaysOn;
  const hiddenSources = edited?.hiddenSources ?? derived.hiddenSources;

  const commit = useCallback(
    (next: FilterState) => {
      setEdited(next);
      const body = {
        activeTags: Array.from(next.activeTags),
        alwaysOn: next.alwaysOn,
        hiddenSources: Array.from(next.hiddenSources),
      };
      onSaveFilter?.(body);
      // why: a failed save leaves her mix on screen. The next change writes
      // it again. The filter has no error slot of its own.
      if (reviewId) {
        void saveAskPunttReviewCommentFilter(reviewId, body).catch(
          () => undefined,
        );
      }
    },
    [onSaveFilter, reviewId],
  );
  // why: an expert's method runs in chats they are not part of -- that is the
  // point of it -- so the chat's participants often cannot name them. Only
  // then is the enterprise roster fetched, under the key its other readers share.
  const needsRoster = expertOptions.some((expert) => !expert.name);
  const roster = useQuery({
    queryKey: ["retrieve-users"],
    queryFn: () => getUsers(),
    enabled: needsRoster,
  });

  const sources = useMemo((): ReviewSourceFilterOption[] => {
    if (expertOptions.length === 0) return [];
    const rosterNames: Record<string, string> = {};
    for (const user of roster.data ?? []) {
      const label = user.name?.trim() || user.email?.trim();
      if (label) rosterNames[user._id] = label;
    }
    const experts = expertOptions.map((expert) => {
      const name = expert.name ?? rosterNames[expert.expertId];
      return {
        key: expert.key,
        label: name
          ? t("askV2.review.sourceExpert", { name })
          : t("askV2.review.sourceExpertUnnamed"),
      };
    });
    // Puntt's review duplicates Always-on checks; only experts get a Raised by row.
    return experts;
  }, [roster.data, t, expertOptions]);

  const toggleTag = useCallback(
    (tag: ReviewCommentTagId, checked: boolean) => {
      const next = new Set(activeTags);
      if (checked) next.add(tag);
      else next.delete(tag);
      commit({ activeTags: next, alwaysOn, hiddenSources });
    },
    [activeTags, alwaysOn, commit, hiddenSources],
  );

  const toggleAlwaysOn = useCallback(
    (checked: boolean) => {
      commit({ activeTags, alwaysOn: checked, hiddenSources });
    },
    [activeTags, commit, hiddenSources],
  );

  const toggleSource = useCallback(
    (source: ReviewCommentSourceKey, checked: boolean) => {
      // why: no Raised-by row toggles puntt, so accepting the key would write
      // it back into the saved filter with nothing on screen to clear it.
      if (source === PUNTT_FINDINGS_SOURCE) return;
      const next = new Set(hiddenSources);
      if (checked) next.delete(source);
      else next.add(source);
      commit({ activeTags, alwaysOn, hiddenSources: next });
    },
    [activeTags, alwaysOn, commit, hiddenSources],
  );

  const toggleStatus = useCallback(
    (status: ReviewCommentGroup, checked: boolean) =>
      setStatuses((current) => {
        const next = new Set(current);
        if (checked) next.add(status);
        else next.delete(status);
        return next;
      }),
    [],
  );
  const resetStatuses = useCallback(() => setStatuses(EVERY_STATUS), []);
  const statusFilter = useMemo(
    (): ReviewListStatusFilter => ({
      shown: statuses,
      onToggle: toggleStatus,
      onReset: resetStatuses,
    }),
    [resetStatuses, statuses, toggleStatus],
  );

  // Every type, Always-on, every raiser and every status: the filter's own Reset.
  const reset = useCallback(() => {
    setStatuses(EVERY_STATUS);
    commit({
      activeTags: defaultActiveReviewCommentTags(),
      alwaysOn: true,
      hiddenSources: new Set(),
    });
  }, [commit]);

  const sourceFilterOn = expertOptions.length > 0;
  const filterActive = isReviewCommentFilterActive(
    activeTags,
    alwaysOn,
    hiddenSources,
    expertKeys,
  );
  const statusNarrowed = statusFilterNarrows(statuses);
  const isVisible = useCallback(
    (comment: CommentWithOptionalTags) =>
      (!statusNarrowed || statuses.has(reviewCommentGroup(comment))) &&
      isReviewCommentVisible(
        comment,
        activeTags,
        hiddenSources,
        alwaysOn,
        sourceFilterOn,
        filterActive,
      ),
    [
      activeTags,
      alwaysOn,
      filterActive,
      hiddenSources,
      sourceFilterOn,
      statusNarrowed,
      statuses,
    ],
  );

  // why: a review whose findings carry no tags yet still draws the filter, so
  // a person can narrow once tags arrive (untagged rows stay visible).
  const showTagFilter = comments.some((comment) => !comment.isDeleted);

  return {
    activeTags,
    alwaysOn,
    toggleTag,
    toggleAlwaysOn,
    reset,
    statusFilter,
    isVisible,
    showTagFilter,
    sourceFilter:
      sources.length > 0
        ? { sources, hidden: hiddenSources, onToggle: toggleSource }
        : undefined,
  };
}
